import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  detectPlatform,
  generateBurokAppLinkNativeDeeplink,
  storeHrefForPlatform,
} from '../src/utils/burokLinkDeeplink';
import {
  resolveShortCodeOpenTarget,
  type ResolvedOpenTarget,
} from '../src/utils/shortLinkResolve';
import OpenInAppInterstitial from './OpenInAppInterstitial';

const BUROK_LOGO = '/assets/images/BUROKLOGOTWITTER.png';
const BUROK_BACKGROUND = '/assets/images/Burok gradient.png';

/** Accueil minimal sur burok.app/link (sans code). */
export function LinkHomePage() {
  const platform = useMemo(() => detectPlatform(), []);
  const storeHref = storeHrefForPlatform(platform);

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-6 text-white bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${BUROK_BACKGROUND}")` }}
    >
      <img
        src={BUROK_LOGO}
        alt="Burok"
        className="w-24 h-24 mb-6 rounded-2xl shadow-lg"
      />
      <h1 className="text-2xl font-semibold text-center mb-2">Liens Burok</h1>
      <p className="text-center text-slate-300 max-w-md mb-8 text-[15px] leading-relaxed">
        Les liens courts sur ce domaine ouvrent l’application Burok. Scannez ou ouvrez un lien
        reçu pour continuer.
      </p>
      <a
        href={storeHref}
        className="inline-flex items-center justify-center gap-3 px-5 py-3 rounded-xl bg-black text-white font-medium hover:bg-neutral-900 transition-colors shadow-lg"
      >
        <img
          src={BUROK_LOGO}
          alt=""
          className="w-8 h-8 rounded-lg object-contain shrink-0"
          aria-hidden
        />
        Installer Burok
      </a>
    </div>
  );
}

/**
 * burok.app/link/:shortCode → RPC short_links puis ouverture app / redirect pay.
 */
export function LinkOpenPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [target, setTarget] = useState<ResolvedOpenTarget | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const code = shortCode?.trim();
      if (!code) {
        if (!cancelled) {
          setTarget({ openUrl: 'burokapp://', resolved: false, error: 'invalid_code' });
          setLoading(false);
        }
        return;
      }
      const resolved = await resolveShortCodeOpenTarget(code);
      if (!cancelled) {
        setTarget(resolved);
        setLoading(false);
      }
    }
    setLoading(true);
    void run();
    return () => {
      cancelled = true;
    };
  }, [shortCode]);

  if (!shortCode?.trim()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-slate-50">
        <p className="text-slate-600">Lien invalide.</p>
      </div>
    );
  }

  const openUrl =
    target?.openUrl || generateBurokAppLinkNativeDeeplink(shortCode);

  const notFound =
    target && !target.resolved && target.error === 'not_found'
      ? 'Lien introuvable ou expiré. Vous pouvez quand même tenter d’ouvrir l’app.'
      : null;

  return (
    <OpenInAppInterstitial
      openUrl={openUrl}
      webRedirect={target?.webRedirect}
      loading={loading}
      errorMessage={notFound}
    />
  );
}
